import {
  faChevronDown,
  faChevronUp,
  faListCheck,
} from "@fortawesome/free-solid-svg-icons";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactNode } from "react";
import { cn } from "../ClassName";

/**
 * The control that folds a branching assessment's answers away.
 *
 * Exported so a form can put it on its outcome banner instead of on the count
 * bar: where the interview is long the bar scrolls out of reach, and the verdict
 * is what a provider looks at last.
 */
export function AnswersToggle(props: {
  collapsed: boolean;
  onToggle: () => void;
  dataTestId?: string;
}) {
  return (
    <button
      type="button"
      className="tw-shrink-0 tw-cursor-pointer tw-whitespace-nowrap tw-border-none tw-bg-transparent tw-p-0 tw-text-[13px] tw-text-blue-1"
      onClick={props.onToggle}
      data-testid={props.dataTestId}
    >
      {props.collapsed ? "View answers" : "Collapse answers"}
      <FontAwesomeIcon
        icon={props.collapsed ? faChevronDown : faChevronUp}
        className="tw-ml-[3px] tw-text-[11px]"
      />
    </button>
  );
}

interface Props {
  /** How many questions the provider has answered so far. */
  answeredCount: number;
  /** Whether the answers are hidden. Held by the caller so that a "Start over"
   *  control can put the section back to showing them. */
  collapsed: boolean;
  /** Omit where the caller draws the toggle itself. */
  onToggle?: () => void;
  /** The answered questions. Rendered only while expanded. */
  children?: ReactNode;
  className?: string;
  /** Prefix for the `data-testid`s on the bar and its toggle. */
  dataTestId?: string;
}

/**
 * A branching assessment's answered questions, with a bar that folds them away.
 *
 * Show this once the assessment has reached an outcome. Until then the provider
 * is still working through the questions and there is nothing to summarise --
 * folding them away mid-interview would hide the one question they need.
 *
 * The bar states the count rather than letting each caller word its own, so a
 * form cannot end up saying "7 answered" where another says "7 of 12 complete".
 * Wording that varies between assessments is wording providers have to re-read.
 */
export function AnsweredQuestions(props: Props) {
  const { answeredCount, collapsed, onToggle, children, dataTestId } = props;

  return (
    <div className={cn("tw-flex tw-flex-col tw-gap-[10px]", props.className)}>
      {/* One string: prettier-plugin-tailwindcss eats the trailing space that
          concatenated pieces need, gluing two class names together. */}
      <div
        className="tw-flex tw-items-center tw-gap-[10px] tw-rounded-[4px] tw-border tw-border-solid tw-border-gray-light-1 tw-bg-gray-light-3 tw-px-[16px] tw-py-[12px] tw-text-[13px] tw-text-gray-2"
        data-testid={dataTestId}
      >
        <FontAwesomeIcon icon={faListCheck} className="tw-text-gray-3" />
        <span className="tw-flex-1">
          {answeredCount} {answeredCount === 1 ? "question" : "questions"}{" "}
          answered
        </span>
        {onToggle ? (
          <AnswersToggle
            collapsed={collapsed}
            onToggle={onToggle}
            dataTestId={dataTestId ? `${dataTestId}-toggle` : undefined}
          />
        ) : null}
      </div>
      {collapsed ? null : children}
    </div>
  );
}
